import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button, { ButtonProps } from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/system';
import { t } from 'i18next';
import { ReactNode } from 'react';

interface DataGridBottomBarProps {
  elements?: ReactNode;
  onClearSelectedRows?: () => void;
  selectedRowIDs: string[];
}

export default function DataGridBottomBar({
  elements,
  onClearSelectedRows,
  selectedRowIDs,
}: DataGridBottomBarProps) {
  const theme = useTheme();
  const shouldShowBar = Boolean(
    selectedRowIDs.length && (onClearSelectedRows || elements),
  );

  if (!shouldShowBar) {
    return <></>;
  }

  return (
    <Box
      sx={{
        bottom: theme.spacing(10),
        display: 'flex',
        justifyContent: 'center',
        left: 0,
        position: 'fixed',
        right: 0,
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: theme.palette.grey[900],
          borderRadius: 1,
          boxShadow: '0px 2px 40px 0px rgba(0, 0, 0, 0.40);',
          display: 'flex',
          gap: 1,
          p: 1,
          pointerEvents: 'auto',
        }}
      >
        {onClearSelectedRows && (
          <DataGridBottomBarButton
            endIcon={<Close />}
            onClick={onClearSelectedRows}
            isSelectedRowsCountButton
          >
            {t('common.count_selected', { count: selectedRowIDs.length })}
          </DataGridBottomBarButton>
        )}

        {elements}
      </Box>
    </Box>
  );
}

interface DataGridBottomBarButtonProps extends Omit<ButtonProps, 'size' | 'sx'> {
  isSelectedRowsCountButton?: boolean;
}

const StyledDataGridBottomBarButton = styled(Button, {
  shouldForwardProp: (propName) => propName !== 'isSelectedRowsCountButton',
})<DataGridBottomBarButtonProps>(({ isSelectedRowsCountButton, theme, color }) => {
  let overrides = {};

  if (isSelectedRowsCountButton) {
    overrides = {
      backgroundColor: theme.palette.grey[900],
      border: `dashed 1px ${theme.palette.grey[700]}`,
      color: theme.palette.grey[50],
      '&.MuiButton-root:hover': { backgroundColor: theme.palette.grey[800] },
    };
  }

  return {
    backgroundColor: color ? theme.palette[color].main : theme.palette.primary.main,
    borderRadius: theme.spacing(0.5),
    color: color ? theme.palette[color].contrastText : theme.palette.primary.contrastText,
    fontSize: '12px',
    height: '28px',
    letterSpacing: '0.01px',
    minWidth: '90px',
    ...overrides,
    '&.MuiButton-root': {
      '&.Mui-disabled': {
        backgroundColor: color ? theme.palette[color].light : theme.palette.grey[800],
        color: theme.palette.grey[50],
        cursor: 'not-allowed !important',
        pointerEvents: 'auto',
        opacity: color ? 0.8 : 1,
      },
      '& .MuiButton-endIcon .MuiSvgIcon-root': { fontSize: '15px' },
    },
  };
});

function DataGridBottomBarButton(props: DataGridBottomBarButtonProps) {
  return <StyledDataGridBottomBarButton size="small" {...props} />;
}
